import React, { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { CourseCatalog, RemoteActivity, RemoteOrganizer } from '../types';
import { SelectedKrets, selectKretsAction } from './krets-actions';

interface KretsFilterProps {
  catalog: CourseCatalog[];
  organizers: RemoteOrganizer[];
  selectedKretsar: SelectedKrets[];
}

function flatten(array = [], depth = 1): number[] {
  const result = [];
  for (const item of array) {
    if (Array.isArray(item) && depth > 0) {
      const flat = flatten(item, depth - 1);
      for (const i of flat) {
        result.push(i);
      }
    } else {
      result.push(item);
    }
  }
  return result;
}

function extractActivityOrganizers(activities: RemoteActivity[]): number[] {
  const allOrganizers = activities.map((a) => parseInt(a.organizer, 10));
  const organizers = new Set<number>(allOrganizers).values();
  const list = [];
  for (const organizer of organizers) {
    list.push(organizer);
  }
  return list;
}

function extractCatalogOrganizers(catalog: CourseCatalog[]): number[] {
  try {
    return [
      ...flatten(
        catalog.map((c) => extractActivityOrganizers(c.activities)),
        1
      ),
    ];
  } catch (e) {
    console.error('Failed to extract organizers ' + e.message, e);
    return [];
  }
}

interface Props {
  organizationId?: string;
}

export default function KretsFilter({ organizationId }: Props): ReactElement {
  const { catalog, organizers, selectedKretsar } = useSelector(
    ({ catalog, organizers, selectedKretsar }: KretsFilterProps): KretsFilterProps => ({
      catalog,
      organizers,
      selectedKretsar,
    })
  );
  const availableOrganizers = useMemo(() => extractCatalogOrganizers(catalog), [catalog]);
  const dispatch = useDispatch();
  const selectKretsar = useCallback((selected: SelectedKrets[]) => dispatch(selectKretsAction(selected)), [dispatch]);
  useEffect(() => {
    if (organizationId && organizers.length > 0) {
      const org = parseInt(organizationId, 10);
      const item = organizers.find((o: RemoteOrganizer): boolean => {
        return o.id === org;
      });
      if (item) {
        selectKretsar([
          {
            label: item.name,
            value: item.id,
          },
        ]);
      }
    }
  }, [organizationId, organizers, selectKretsar]);
  const style: { display?: string } = {};
  if (organizationId) {
    style.display = 'none';
  }
  return (
    <div style={style}>
      <Select
        isClearable
        isMulti
        onChange={selectKretsar}
        value={selectedKretsar}
        options={organizers
          .filter((o) => availableOrganizers.includes(o.id))
          .map((item) => ({
            label: item.name,
            value: item.id,
          }))}
        placeholder="Filtrera krets"
      />
    </div>
  );
}
KretsFilter.displayName = 'KretsFilter';
